// Welcome and sign-in (UI kit W6). A first visit can try Paidlark straight
// away as a guest, or sign in. A guest can back up later from /sign-in,
// which links Google or email to the same account, so nothing is lost.
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { isEmailAddress } from '@/core/client';
import { track } from '@/data/analytics';
import { AccountExists, type AuthError, AuthFailure, continueWithEmail, forgetPendingSignIn, linkGoogle, sendPasswordReset, type SignInMethod, signInInstead, signOut } from '@/data/authService';
import { t } from '@/l10n/strings';
import { Button, LogoMark, TextField } from '@/ui/controls';
import { Dialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';

const AUTH_WORDS: Record<AuthError, string> = {
  invalidEmail: 'authInvalidEmail',
  weakPassword: 'authWeakPassword',
  wrongCredentials: 'authWrongCredentials',
  userDisabled: 'authUserDisabled',
  tooManyRequests: 'authTooManyRequests',
  noInternet: 'authNoInternet',
  methodNotEnabled: 'authMethodNotEnabled',
  recentLoginNeeded: 'authRecentLoginNeeded',
  signInFailed: 'authSignInFailed',
  emailTakenWrongPassword: 'authEmailTakenWrongPassword',
  popupBlocked: 'webAuthPopupBlocked',
};

export const authErrorWords = (reason: AuthError) => t(AUTH_WORDS[reason]);

export function WelcomePage({ mode }: { mode: 'welcome' | 'backup' }) {
  const { user, invoices = [], business } = useAppData();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState<SignInMethod | 'reset' | 'instead' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [conflict, setConflict] = useState<SignInMethod | null>(null);
  usePageTitle(mode === 'welcome' ? t('welcomeCreateAnInvoiceNow') : t('signInSignInOrCreateAn'));

  const signedIn = user && !user.isGuest;
  const done = (method: SignInMethod, isNew: boolean) => {
    track(isNew ? 'sign_up' : 'login', { method });
    navigate(business ? '/home' : '/setup', { replace: true });
  };

  const failed = (method: SignInMethod, e: unknown) => {
    if (e instanceof AccountExists) {
      setConflict(e.method);
      return;
    }
    const reason = e instanceof AuthFailure ? e.reason : 'signInFailed';
    track('sign_in_failed', { method, reason });
    setError(authErrorWords(reason));
  };

  const google = async () => {
    setError(null);
    setBusy('google');
    try {
      if (await linkGoogle()) done('google', true);
    } catch (e) {
      failed('google', e);
    } finally {
      setBusy(null);
    }
  };

  const withEmail = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const errors = {
      email: !email.trim() ? t('errorEmailMissing') : !isEmailAddress(email) ? t('errorEmailInvalid') : undefined,
      password: password.length < 8 ? t('errorPasswordTooShort') : undefined,
    };
    setFieldErrors(errors);
    if (errors.email || errors.password) return;
    setBusy('email');
    try {
      await continueWithEmail(email.trim(), password);
      done('email', true);
    } catch (err) {
      failed('email', err);
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    setError(null);
    if (!isEmailAddress(email)) {
      setFieldErrors({ email: t('authEnterEmailFirst') });
      return;
    }
    setBusy('reset');
    try {
      await sendPasswordReset(email);
      setResetSentTo(email.trim());
    } catch {
      setError(t('authResetEmailFailed'));
    } finally {
      setBusy(null);
    }
  };

  const instead = async () => {
    const method = conflict ?? 'email';
    setBusy('instead');
    try {
      await signInInstead();
      setConflict(null);
      track('login', { method });
      navigate('/home', { replace: true });
    } catch (e) {
      setConflict(null);
      failed(method, e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 640px)', minHeight: '100vh' }} className="welcome">
      <BrandPanel />
      <main className="welcome__form" style={{ display: 'grid', placeItems: 'center', padding: '48px 24px' }}>
        <div className="stack-4" style={{ width: 'min(400px, 100%)' }}>
          <div className="welcome__mobile-logo">
            <LogoMark size={40} />
          </div>
          {signedIn ? (
            <>
              <div className="stack-2">
                <h1 className="display">{t('settingsBackedUp')}</h1>
                <p className="body muted">{t('signInYourInvoicesAreBackedUp')}</p>
                {user.email && <p className="body-strong">{user.email}</p>}
              </div>
              <Link to="/home" className="btn btn--action btn--lg btn--block">
                {t('webGoToDashboard')}
              </Link>
              <Button
                variant="ghost"
                block
                onClick={async () => {
                  await signOut();
                  track('sign_out');
                  navigate('/welcome', { replace: true });
                }}
              >
                {t('commonSignOut')}
              </Button>
            </>
          ) : (
            <>
              <div className="stack-2">
                <h1 className="display">
                  {mode === 'backup' ? (
                    t('backupTitle', { count: invoices.length })
                  ) : (
                    <>
                      {t('webSignInHeadlineStart')}
                      <em>{t('webSignInHeadlineEnd')}</em>
                    </>
                  )}
                </h1>
                <p className="body muted">{mode === 'backup' ? t('webBackupBody') : t('webSignInBody')}</p>
              </div>
              {error && (
                <div className="banner banner--error" role="alert">
                  <Icon name="info" />
                  <div className="banner__txt">
                    <b>{error}</b>
                  </div>
                </div>
              )}
              <Button variant="secondary" size="lg" block onClick={google} busy={busy === 'google'} disabled={busy !== null}>
                {busy !== 'google' && <Icon name="google" size={20} />}
                {t('signInContinueWithGoogle')}
              </Button>
              <div className="row">
                <span className="divider grow" />
                <span className="caption">{t('signInOrUseEmail')}</span>
                <span className="divider grow" />
              </div>
              {resetSentTo ? (
                <div className="banner banner--info" role="status">
                  <Icon name="mail" />
                  <div className="banner__txt">
                    <span style={{ color: 'var(--ink)' }}>{t('signInWeSentALinkTo', { resetSentTo })}</span>
                  </div>
                </div>
              ) : null}
              <form className="stack-4" onSubmit={withEmail} noValidate>
                <TextField label={t('signInEmail')} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fieldErrors.email} />
                <div className="field">
                  <span className="field__label">
                    <label htmlFor="password">{t('signInPassword')}</label>
                    <button type="button" className="link-btn" onClick={reset} disabled={busy !== null}>
                      {t('signInForgotPassword')}
                    </button>
                  </span>
                  <span className="input input--affix" aria-invalid={fieldErrors.password ? true : undefined}>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete={mode === 'welcome' ? 'current-password' : 'new-password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={fieldErrors.password ? true : undefined}
                      aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                    />
                    <button type="button" className="iconbtn iconbtn--sm" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? t('signInHidePassword') : t('signInShowPassword')}>
                      <Icon name="eye" size={18} />
                    </button>
                  </span>
                  {fieldErrors.password && (
                    <span className="error" id="password-error" role="alert">
                      {fieldErrors.password}
                    </span>
                  )}
                </div>
                <Button type="submit" variant="action" size="lg" block busy={busy === 'email'} disabled={busy !== null}>
                  {t('signInContinueWithEmail')}
                </Button>
                <p className="caption" style={{ textAlign: 'center' }}>
                  {t('signInNewToPaidlarkContinuingCreates')}
                </p>
              </form>
              {mode === 'welcome' ? (
                <Link to="/setup" className="link-btn" style={{ textAlign: 'center' }} onClick={() => track('welcome_choice', { choice: 'start_free' })}>
                  {t('webTryWithoutAccount')}
                </Link>
              ) : (
                <Link to="/home" className="link-btn" style={{ textAlign: 'center', color: 'var(--muted)' }}>
                  {t('commonNotNow')}
                </Link>
              )}
            </>
          )}
        </div>
      </main>
      <Dialog
        open={conflict !== null}
        onClose={() => {
          forgetPendingSignIn();
          setConflict(null);
        }}
        title={t('signInConflictTitle')}
        actions={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                forgetPendingSignIn();
                setConflict(null);
              }}
            >
              {t('signInUseAnotherAccount')}
            </Button>
            <Button variant="action" onClick={instead} busy={busy === 'instead'}>
              {t('signInAnyway')}
            </Button>
          </>
        }
      >
        <p className="body ink2">
          {conflict === 'email'
            ? t('signInConflictEmail', { count: Math.max(invoices.length, 1) })
            : t('signInConflictAccount', { provider: t('signInMethodGoogle'), count: Math.max(invoices.length, 1) })}
        </p>
      </Dialog>
    </div>
  );
}

/** The dark half: the promise, and an invoice that's been paid. */
function BrandPanel() {
  return (
    <section className="welcome__brand" aria-hidden>
      <svg className="welcome__orbit" width="560" height="560" viewBox="0 0 560 560">
        <circle cx="280" cy="280" r="270" fill="none" stroke="#19D38A" strokeOpacity=".14" />
        <circle cx="280" cy="280" r="200" fill="none" stroke="#19D38A" strokeOpacity=".1" />
        <circle cx="280" cy="280" r="130" fill="none" stroke="#D9AE55" strokeOpacity=".2" />
        <circle cx="550" cy="280" r="7" fill="#19D38A" />
      </svg>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, font: '400 27px/1 var(--font-serif)' }}>
        <LogoMark size={40} />
        {t('appName')}
      </span>
      <div className="welcome__card">
        <div className="between">
          <span style={{ font: '600 12px/1 var(--font-sans)', color: '#0F4A37', letterSpacing: '.06em' }}>{t('welcomeSampleInvoiceNumber')}</span>
          <span className="chip" style={{ background: '#E2EEE6', color: '#0F6A4A' }}>
            <i />
            {t('statusPaid')}
          </span>
        </div>
        <div style={{ height: 8, width: '70%', borderRadius: 4, background: '#EEF0EE', marginTop: 16 }} />
        <div style={{ height: 8, width: '50%', borderRadius: 4, background: '#EEF0EE', marginTop: 8 }} />
        <div className="between" style={{ marginTop: 22 }}>
          <span style={{ font: '500 13px/1 var(--font-sans)', color: '#62665F' }}>{t('welcomeTotal')}</span>
          <span style={{ font: '400 38px/1 var(--font-serif)' }}>₦485,000</span>
        </div>
      </div>
      <div className="stack-2">
        <h2 className="display-xl" style={{ fontSize: 54, lineHeight: '56px', maxWidth: 560 }}>
          {t('welcomeHeadlineStart')}
          <em style={{ color: 'var(--signal)' }}>{t('welcomeHeadlineEnd')}</em>
        </h2>
        <p className="body-lg" style={{ color: 'var(--on-surface-strong-muted)' }}>
          {t('signInFreeForeverProWhenYou')}
        </p>
      </div>
    </section>
  );
}
